export type PlaceChoice = {
  id: string;
  name: string;
  fullName: string;
  address: string;
  selectable: boolean;
};
export type PlaceChoices = {
  allowOther: boolean;
  cityName: string;
  places: readonly PlaceChoice[];
};
export type PlaceCardChoice = PlaceChoice & {
  image?: {
    vectorUrl?: string | null;
    previewUrl?: string | null;
    cardUrl?: string | null;
  } | null;
  reason?: string;
};
export function renderPlaceCard(view: {
  place: PlaceCardChoice;
  selected: boolean;
  recommended: boolean;
  labels: {
    recommended: string;
    fixed: string;
    unknown: string;
    inactive: string;
  };
  escape: (value: unknown) => string;
}): string {
  const { place, escape: escapeHtml } = view;
  return `<button type="button" class="city-place-option" data-city-place="${escapeHtml(place.id)}" aria-pressed="${view.selected}" ${!place.selectable ? "disabled" : ""}><span class="place-choice-heading">${place.image ? `<img class="place-choice-thumbnail" src="${escapeHtml(place.image.vectorUrl ?? place.image.previewUrl ?? place.image.cardUrl)}" alt="" />` : ""}<strong>${escapeHtml(place.fullName || place.name)}</strong></span>${view.recommended ? `<small class="recommended-place">✓ ${escapeHtml(view.labels.recommended)}</small>` : ""}<span>${escapeHtml(place.address)}</span>${!place.selectable ? `<small>${escapeHtml(place.reason === "outside_fixed_place" ? view.labels.fixed : place.reason === "hours_unknown" ? view.labels.unknown : view.labels.inactive)}</small>` : ""}</button>`;
}
type ViewRoot = Document;
type SelectionMarker = {
  getElement(): HTMLElement | undefined;
  setZIndexOffset(value: number): unknown;
  setOpacity(value: number): unknown;
};

export function renderSelectedPlaceSummaryView(view: {
  document: ViewRoot;
  dateMarkup: string;
  kind?: string;
  options: PlaceChoices | null | undefined;
  selectedId: string;
  fallbackPlace?: PlaceChoice | null;
  phoneLabel: string;
  approvalLabel: string;
  escape: (value: unknown) => string;
}): void {
  const document = view.document;
  const summaryDateMarkup = () => view.dateMarkup;
  const currentPlaceOptions = () => view.options;
  const selectedCityPlaceId = view.selectedId;
  const informationalFixedPlace = () => view.fallbackPlace;
  const escapeHtml = view.escape;
  document
    .querySelector(".meeting-summary .summary-start")
    ?.replaceWith(
      document.createRange().createContextualFragment(summaryDateMarkup()),
    );
  const options = currentPlaceOptions();
  const field = document.querySelector<HTMLElement>(
    ".meeting-summary dl > div:last-child dd",
  );
  if (!field) return;
  if (view.kind === "phone") {
    field.textContent = view.phoneLabel;
    field.parentElement?.classList.remove("summary-city-place");
    return;
  }
  if (!options) return;
  field.parentElement?.classList.add("summary-city-place");
  const place =
    options.places.find((place) => place.id === selectedCityPlaceId) ??
    informationalFixedPlace();
  if (place) {
    field.innerHTML = `<span>${escapeHtml(place.fullName || place.name)}</span><span class="summary-address">${escapeHtml(place.address)}</span>`;
  } else
    field.textContent =
      selectedCityPlaceId === "other" ? view.approvalLabel : options.cityName;
}

export function updatePlaceSelectionView(view: {
  document: ViewRoot;
  options: PlaceChoices | null | undefined;
  selectedId: string;
  markers: Iterable<[string, SelectionMarker]>;
  ownAddressMarkup: () => string;
  wireVisitorAddress: () => void;
  onSummaryUpdate: () => void;
}): void {
  const document = view.document;
  const currentPlaceOptions = () => view.options;
  const selectedCityPlaceId = view.selectedId;
  const cityPlaceMarkers = view.markers;
  const ownAddressMarkup = view.ownAddressMarkup;
  const wireVisitorAddress = view.wireVisitorAddress;
  const renderSelectedPlaceSummary = view.onSummaryUpdate;
  document
    .querySelectorAll<HTMLButtonElement>("[data-city-place]")
    .forEach((button) => {
      button.disabled =
        button.dataset.cityPlace === "other"
          ? !currentPlaceOptions()?.allowOther
          : !currentPlaceOptions()?.places.some(
              (p) => p.id === button.dataset.cityPlace && p.selectable,
            );
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.cityPlace === selectedCityPlaceId),
      );
    });
  for (const [id, marker] of cityPlaceMarkers) {
    const selected = id === selectedCityPlaceId;
    marker
      .getElement()
      ?.querySelector(".city-place-marker-image, .city-place-marker")
      ?.classList.toggle("selected", selected);
    marker.setZIndexOffset(selected ? 1000 : 0);
    if (id === "other") marker.setOpacity(selected ? 1 : 0);
  }
  const list = document.querySelector(".city-place-list");
  if (
    selectedCityPlaceId === "other" &&
    !list?.querySelector(".visitor-address")
  ) {
    list
      ?.querySelector(".other-place-card")
      ?.insertAdjacentHTML("beforeend", ownAddressMarkup());
    wireVisitorAddress();
  } else if (selectedCityPlaceId !== "other")
    list?.querySelector(".visitor-address")?.remove();
  renderSelectedPlaceSummary();
}

export function bindPlaceCards(
  document: ViewRoot,
  onChoosePlace: (id: string) => void | Promise<void>,
): void {
  document
    .querySelectorAll<HTMLButtonElement>("[data-city-place]")
    .forEach((button) => {
      button.onclick = () => {
        void onChoosePlace(button.dataset.cityPlace ?? "");
      };
    });
}

export function mountPlaceSelectionPrompt(view: {
  document: Document;
  panel: Element | null;
  message: string;
  mode: "city" | "personal";
}): void {
  const { document, panel } = view;
  const notice = document.createElement("p");
  notice.setAttribute("role", "status");
  notice.textContent = view.message;
  if (view.mode === "city") {
    panel
      ?.querySelectorAll(".slot-list, [role='status'], #retry-city-day")
      .forEach((node) => node.remove());
    panel?.append(notice);
  } else {
    notice.id = "choose-place-first";
    panel?.querySelector(".day-slots-heading, h3")?.after(notice);
    panel?.querySelectorAll(".slot-list").forEach((node) => node.remove());
    document.querySelectorAll<HTMLButtonElement>(".slot").forEach((button) => {
      button.setAttribute("aria-disabled", "true");
      button.setAttribute("aria-describedby", notice.id);
    });
  }
}
